import styled from '@emotion/styled';
import { Plus } from 'lucide-react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import UDButton from 'ud-ui/components/button';
import { StageV2 } from 'modules/championship/domain/interfaces/stage';

import { ChampionshipStateData } from 'modules/championship/store/championship';
import { showChampionshipStageForms } from 'modules/championship/store/championship/actions';
import {
  getStagesChampionshipIdSelector,
  isStagesIdleSelector,
  isStagesLoadedSelector,
  isStagesLoadingSelector,
  stagesActions,
  stageSelectors,
} from 'modules/championship/store/stages';
import { fetchStages } from 'modules/championship/store/stages/stages.actions';
import StageCard from './stage-card';
import StageForms from './stage-forms';
import { StagesGrid } from './stages-grid';
import { StagesGridSkeleton } from './stages-grid-skeleton';
import { theme } from 'styles/theme/theme';

const StyledAddStageBlock = styled.div`
  border: 2px dashed ${theme.colors.SurfaceRomanSilver30};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 157px;
`;

export const StyledAddStageButton = styled(UDButton)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

type StagesProps = {
  championship: ChampionshipStateData;
};

const Stages = ({ championship }: StagesProps) => {
  const dispatch = useDispatch();

  const isIdle = useSelector(isStagesIdleSelector);
  const isLoading = useSelector(isStagesLoadingSelector);
  const isLoaded = useSelector(isStagesLoadedSelector);
  const stages = useSelector(stageSelectors.selectAll);
  const loadedStagesChampionshipId = useSelector(getStagesChampionshipIdSelector);

  if (championship && (isIdle || (isLoaded && loadedStagesChampionshipId !== championship.id))) {
    dispatch(fetchStages(championship.id));
  }

  const { showStageForms } = championship;

  const handleAddStageClick = useCallback(() => {
    dispatch(
      showChampionshipStageForms({
        state: true,
        championshipId: championship.id,
      }),
    );
  }, [dispatch, championship.id]);

  const handleStageSelect = useCallback(
    (stage: StageV2) => () => {
      dispatch(stagesActions.stageSelected(stage.id));
      dispatch(
        showChampionshipStageForms({
          state: true,
          championshipId: championship.id,
        }),
      );
    },
    [dispatch, championship.id],
  );

  if (isLoading || !championship) {
    return (
      <div className="w-full px-6">
        <StagesGridSkeleton />
      </div>
    );
  }

  if (!showStageForms) {
    return (
      <div className="flex h-full w-full flex-col px-6">
        <StagesGrid>
          {stages?.map((stage) => (
            <StageCard key={stage.id} stage={stage} onSelect={handleStageSelect(stage)} />
          ))}
          <StyledAddStageBlock>
            <StyledAddStageButton className="mt-3 mb-3 w-1/2" variant="dark" type="button" onClick={handleAddStageClick}>
              {/* <UDIcon name="plus" /> */}
              <Plus size={16} />
              &nbsp;Создать этап
            </StyledAddStageButton>
          </StyledAddStageBlock>
        </StagesGrid>
      </div>
    );
  }

  return <StageForms championship={championship} />;
};

export default Stages;
